/**
 * Unlayer exports a full HTML document (`<html><head><style>…</head><body>…`). We render
 * page content inline rather than in an iframe, so the outer document wrappers have to go —
 * nesting a document inside the app is invalid — while the page's own `<style>` blocks are
 * kept, since they carry the design.
 *
 * Fragments pass through untouched.
 *
 * The extracted `<style>` blocks are not scoped, so they must not be injected into the app
 * document directly (they bleed into the chrome — e.g. the top bar). `ShadowContent` renders
 * this output inside a shadow root, which contains them while keeping rendering inline.
 */
export const extractRenderableHtml = (raw: string): string => {
  if (!raw) {
    return '';
  }
  if (!/<html[\s>]/i.test(raw) && !/<body[\s>]/i.test(raw)) {
    return raw; // already a fragment
  }
  try {
    const doc = new DOMParser().parseFromString(raw, 'text/html');
    const styles = Array.from(doc.head?.querySelectorAll('style') ?? [])
      .map((s) => s.outerHTML)
      .join('');
    return styles + (doc.body?.innerHTML ?? '');
  } catch {
    return raw;
  }
};
